import React from 'react';
import styled from 'styled-components';
import { Pencil } from 'lucide-react';
import Button from '../../../../components/ui/Button';
import { sectionNumber } from '../reportModel';

// Rapor bölümlerinin ortak iskeleti: kaş (01 · …), başlık, "Nasıl Okunmalı?" kutusu, düzenle düğmesi, ızgara hücreleri.
// Renkler theme.colors.report (colors.js) — sabit renk YOK.

const Box = styled.div`
  display: flex;
  gap: 20px;
  padding: 14px 18px;
  margin-bottom: 20px;
  background: ${({ theme }) => theme.colors.report.noteBg};
  border: 1px solid ${({ theme }) => theme.colors.report.noteBorder};
  border-radius: 8px;
`;

const BoxTitle = styled.div`
  flex: 0 0 96px;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.report.noteTitle};
`;

const BoxBody = styled.div`
  flex: 1;
  font-size: 13px;
  line-height: 1.55;
  color: ${({ theme }) => theme.colors.report.text};
`;

export default function HowToRead({ t, children }) {
  return (
    <Box data-testid="how-to-read">
      <BoxTitle>{t('survey.report.howToRead')}</BoxTitle>
      <BoxBody>{children}</BoxBody>
    </Box>
  );
}

const Section = styled.section`
  background: ${({ theme }) => theme.colors.report.paper};
  border-radius: 8px;
  padding: 28px 32px;
  margin-bottom: 20px;
  box-shadow: 0 1px 4px rgba(${({ theme }) => theme.colors.shadow.rgb}, 0.06);
  font-family: ${({ theme }) => theme.metrics.font.family};
  color: ${({ theme }) => theme.colors.report.text};
`;

const Eyebrow = styled.div`
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.report.noteTitle};
`;

const HeadRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin: 4px 0 16px;
`;

const Title = styled.h2`
  margin: 0;
  font-size: 26px;
  font-weight: 800;
  line-height: 1.2;
  color: ${({ theme }) => theme.colors.report.brand};
`;

export function SectionShell({ id, title, subtitle, actions, children }) {
  return (
    <Section data-section={id}>
      <Eyebrow>{sectionNumber(id)}{subtitle ? ` · ${subtitle}` : ''}</Eyebrow>
      <HeadRow>
        <Title>{title}</Title>
        {actions ? <div>{actions}</div> : null}
      </HeadRow>
      {children}
    </Section>
  );
}

export function EditButton({ t, onClick, testId }) {
  return (
    <Button variant="ghost" onClick={onClick} data-testid={testId}>
      <Pencil size={14} /> {t('survey.report.edit')}
    </Button>
  );
}

export const SubHeading = styled.h3`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin: 20px 0 10px;
  font-size: 16px;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.report.brand};
`;

export const Panel = styled.div`
  background: ${({ theme }) => theme.colors.report.panel};
  border-radius: 8px;
  padding: 16px 20px;
  margin-top: 16px;
  font-size: 14px;
  line-height: 1.6;
`;

export const Muted = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.report.muted};
`;

// CSS grid tablo — `<table>` yasak (frontend/CLAUDE.md). $cols: grid-template-columns değeri.
export const Grid = styled.div`
  display: grid;
  grid-template-columns: ${({ $cols }) => $cols};
  align-items: center;
  column-gap: 12px;
  font-size: 13px;
`;

export const Cell = styled.div`
  padding: 8px 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.report.grayLight};
  min-width: 0;
  font-weight: ${({ $bold }) => ($bold ? 800 : 400)};
  text-align: ${({ $align }) => $align || 'left'};
  color: ${({ $muted, theme }) => ($muted ? theme.colors.report.muted : 'inherit')};
`;

export const HeadCell = styled(Cell)`
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.report.muted};
  border-bottom: 1.5px solid ${({ theme }) => theme.colors.report.brand};
`;
